import React from "react";
import Badge from "@/components/ui/badge";
import { cn } from "@/utils/utils";
import { TextVariant } from "@/components/ui/text";

export type scoBadgeSize = "sm" | "lg";

export type KpiScoBadgeTone = "selected" | "positive" | "negative" | "neutral";

// Remaining div props land on the root, so a TooltipTrigger asChild can attach its handlers.
export interface KpiScoBadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  label: string;
  tone: KpiScoBadgeTone;
  onClick?: () => void;
  className?: string;
  textVariant?: TextVariant;
  size?: scoBadgeSize;
}

const TONE_COLORS: Record<KpiScoBadgeTone, { bg: string; text: string }> = {
  selected: {
    bg: "bg-badges-selected",
    text: "text-primary"
  },
  positive: {
    bg: "bg-[rgba(var(--surface-chips-positive-rgb),0.18)]",
    text: "text-charts-positive"
  },
  negative: {
    bg: "bg-[rgba(var(--surface-chips-negative-rgb),0.18)]",
    text: "text-error"
  },
  neutral: {
    bg: "bg-[rgba(var(--surface-chips-natural-rgb),0.20)]",
    text: "text-link"
  }
};

const KpiScoBadge = React.forwardRef<HTMLDivElement, KpiScoBadgeProps>(
  (
    {
      label,
      tone,
      onClick,
      onKeyDown,
      className = "",
      textVariant,
      size = "lg",
      ...rest
    },
    ref
  ) => {
    const { bg, text } = TONE_COLORS[tone];

    // A badge given role="button" activates on Enter and Space like a native button.
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (
        rest.role === "button" &&
        event.target === event.currentTarget &&
        !event.defaultPrevented &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    return (
      <div
        ref={ref}
        {...rest}
        className="relative group"
        onClick={onClick}
        onKeyDown={handleKeyDown}
      >
        <Badge
          text={label}
          textVariant={
            textVariant ? textVariant : size === "sm" ? "smBody" : "label"
          }
          className={cn(
            "border-none gap-0 rounded-md h-7 whitespace-nowrap",
            size === "sm" ? "px-2 py-1" : "px-[10px] py-[6px]",
            bg,
            className
          )}
          textClassName={text}
        />
        {onClick && (
          <div className="absolute inset-0 rounded-lg pointer-events-none bg-on-surface opacity-0 group-hover:opacity-[0.08] group-active:opacity-[0.24]" />
        )}
      </div>
    );
  }
);
KpiScoBadge.displayName = "KpiScoBadge";

export default KpiScoBadge;
